import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { ValidatorPattern } from '../utility/validators.pattern';

@Directive({
  selector: '[appAlphabetsWithNumbers]',
})
export class AppAlphabetsWithNumbersDirective implements OnInit {
  @Input() appAlphabetsWithNumbers!: number;
  private readonly allowNumbers: number = 6; // Maximum digits allowed
  private readonly minNumbers: number = 4;
  private regexPattern!: RegExp;
  private readonly specialKeys: Array<string> = [
    'Backspace',
    'Tab',
    'End',
    'Home',
    'ArrowLeft',
    'ArrowRight',
    'Control',
  ];

  constructor(private readonly el: ElementRef) {}

  ngOnInit() {
    this.updatePattern();
  }

  private updatePattern() {
    // Set pattern for two alphabets followed by 1 to 4 digits
    if (this.appAlphabetsWithNumbers === 2) {
      this.regexPattern = new RegExp(ValidatorPattern.flightValidation);
    } else if (this.appAlphabetsWithNumbers === 3) {
      this.regexPattern = new RegExp(
        ValidatorPattern.ThreeAlphabetsWithNumbers
      );
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = this.el.nativeElement;
    const current: string = input.value;
    const cursorPos = input.selectionStart;
    if (
      cursorPos === null ||
      this.specialKeys.includes(event.key) ||
      (event.ctrlKey && (event.key === 'a' || event.key === 'A'))
    ) {
      return;
    }

    // Block any special character that is not allowed
    if (!/[A-Za-z0-9]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    this.checkForValidation(event, cursorPos, current);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text');
    const input = this.el.nativeElement;
    const current: string = input.value;
    const cursorPos = input.selectionStart;
    if (cursorPos === null || !pastedText) {
      return;
    }
    const next: string = [
      current.slice(0, cursorPos),
      pastedText,
      current.slice(cursorPos),
    ].join('');
    if (!this.isValid(next)) {
      event.preventDefault();
    }
  }

  private isValid(value: string): boolean {
    const firstSection = value.substring(0, this.appAlphabetsWithNumbers);
    const numbers = value.substring(this.appAlphabetsWithNumbers);
    // Must be alphanumeric in the first section
    if (!ValidatorPattern.alphaNumerics.test(firstSection)) {
      return false;
    }

    if (numbers && !/^\d*$/.test(numbers)) {
      return false;
    }
    if (numbers.length > this.allowNumbers) {
      return false;
    }
    // Prevent exceeding overall length
    if (value.length > this.appAlphabetsWithNumbers + this.allowNumbers) {
      return false;
    }

    if (this.regexPattern && !this.regexPattern.test(value)) {
      // Allow partial matches while typing.
      if (numbers.length > 0 && numbers.length < this.minNumbers) {
        // Allow partial typing until reaching min length
        return true;
      }
      return false;
    }
    return true;
  }

  private checkForValidation(
    event: KeyboardEvent,
    cursorPos: number,
    current: string
  ) {
    if (event.key.length === 1) {
      if (cursorPos < this.appAlphabetsWithNumbers) {
        // Allow alphanumerics for the first N characters
        const firstSectionCount = current
          .slice(0, this.appAlphabetsWithNumbers)
          .replaceAll(
            new RegExp(ValidatorPattern.alphaNumerics, 'g'),
            ''
          ).length;

        if (firstSectionCount >= this.appAlphabetsWithNumbers) {
          event.preventDefault();
          return;
        }
      } else if (
        /[A-Za-z]/.test(event.key) || // Block alphabets after first N
        current
          .slice(this.appAlphabetsWithNumbers)
          .replaceAll(new RegExp(ValidatorPattern.NonDigit, 'g'), '').length >=
          this.allowNumbers
      ) {
        event.preventDefault();
        return;
      }
    }
  }
}
